import React, { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { Button } from "reactstrap";
import { publicPieceUrl } from "../../helpers/collectorIds";
import { clipExcerptWords, downloadQrFromSvg, MINIMAL_QR_PX } from "../../helpers/collectorQrDownload";
import { getItemQr } from "../../services/collectorApi";
import notifications from "../../common/Components/Notifications/Notification";

type CardFormat = "minimal" | "luxury";

type Props = {
  itemId?: string;
  collectorItemId?: string;
  title: string;
  story?: string;
  coverPhotoUrl?: string;
  format?: CardFormat;
  onFormatChange?: (format: CardFormat) => void;
  onPrint?: () => void;
  onPreviewCard?: () => void;
};

const CollectorItemQrPanel: React.FC<Props> = ({
  itemId,
  collectorItemId,
  title,
  story,
  coverPhotoUrl,
  format = "luxury",
  onFormatChange,
  onPrint,
  onPreviewCard,
}) => {
  const [qrUrl, setQrUrl] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setError("");
    setCopied(false);
    const immediate = publicPieceUrl(collectorItemId || "");
    if (immediate) {
      setQrUrl(immediate);
    } else {
      setQrUrl("");
    }
    if (!itemId) {
      if (!immediate) {
        setError("This piece does not have a public QR yet.");
      }
      return;
    }
    getItemQr(itemId)
      .then((res) => {
        const data = (res.data?.data || {}) as Record<string, unknown>;
        const id = String(data.collectorItemId || collectorItemId || "");
        const next = publicPieceUrl(id, String(data.qrPayloadUrl || data.publicPath || ""));
        if (next) setQrUrl(next);
      })
      .catch((err) => {
        const code = err?.response?.data?.message || err?.response?.status;
        if (code === "QR_NOT_AVAILABLE" || err?.response?.status === 403) {
          setError("Public cards are only for pieces you keep in My pieces.");
          return;
        }
        if (!immediate) {
          setError("Could not load the QR code. Try again.");
        }
      });
  }, [itemId, collectorItemId]);

  const copyLink = async () => {
    if (!qrUrl) return;
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy the link.");
    }
  };

  const download = async () => {
    if (onPreviewCard) {
      onPreviewCard();
      return;
    }
    const svg = qrRef.current?.querySelector("svg") || null;
    setDownloading(true);
    setError("");
    try {
      await downloadQrFromSvg(svg, {
        title,
        format,
        excerpt: clipExcerptWords(story || ""),
        coverUrl: coverPhotoUrl,
        itemId,
      });
      notifications({
        title: "Downloaded",
        message: format === "luxury" ? "Luxury 4×6 card saved." : "QR card saved.",
        type: "success",
      });
    } catch {
      setError(format === "luxury" ? "Could not download the card." : "Could not download the QR.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <aside className="mc-qr-panel" aria-label="Public QR and link">
      <h2 className="mc-qr-panel-title">Public QR</h2>
      <p className="small text-muted mb-3">
        Only this piece is shown. Your collection stays private. Price and purchase place never appear.
      </p>
      <div className="mc-qr-panel-code" ref={qrRef}>
        {qrUrl ? (
          <QRCode
            value={qrUrl}
            size={MINIMAL_QR_PX}
            style={{ height: MINIMAL_QR_PX, width: MINIMAL_QR_PX }}
            viewBox="0 0 256 256"
            bgColor="#FFFFFF"
            fgColor="#000000"
          />
        ) : (
          <p className="small text-muted mb-0">Preparing QR…</p>
        )}
      </div>
      {error && <p className="mc-field-error" role="alert">{error}</p>}
      <table className="mc-qr-table">
        <caption className="visually-hidden">Public page link and downloads</caption>
        <tbody>
          <tr>
            <th scope="row">Public page</th>
            <td>
              {qrUrl ? (
                <a href={qrUrl} target="_blank" rel="noreferrer" className="mc-qr-url">
                  {qrUrl.replace(/^https?:\/\//, "")}
                </a>
              ) : (
                <span className="text-muted">Unavailable</span>
              )}
              <div className="mc-qr-actions">
                {qrUrl ? (
                  <a className="mc-btn-ghost" href={qrUrl} target="_blank" rel="noreferrer">
                    Open
                  </a>
                ) : null}
                <Button type="button" className="mc-btn-secondary" disabled={!qrUrl} onClick={copyLink}>
                  {copied ? "Copied" : "Copy link"}
                </Button>
              </div>
            </td>
          </tr>
          <tr>
            <th scope="row">Download card</th>
            <td>
              <p className="mb-2 small text-muted">Luxury 4×6 includes the photograph. Minimal is title and QR only.</p>
              <div className="mb-2" role="group" aria-label="Card format">
                <button
                  type="button"
                  className={`mc-toggle me-2 ${format === "minimal" ? "is-active" : ""}`}
                  onClick={() => onFormatChange?.("minimal")}
                >
                  Minimal
                </button>
                <button
                  type="button"
                  className={`mc-toggle ${format === "luxury" ? "is-active" : ""}`}
                  onClick={() => onFormatChange?.("luxury")}
                >
                  Luxury
                </button>
              </div>
              <div className="mc-qr-actions">
                <Button
                  type="button"
                  className="mc-btn-primary"
                  disabled={!qrUrl || downloading}
                  onClick={download}
                >
                  {downloading ? "Downloading…" : format === "luxury" ? "Download card" : "Download QR"}
                </Button>
                {onPrint && (
                  <Button type="button" className="mc-btn-ghost" disabled={!qrUrl} onClick={onPrint}>
                    Print / PDF
                  </Button>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </aside>
  );
};

export default CollectorItemQrPanel;
